import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BackgroundHearts from '../components/BackgroundHearts';

const LoginPage = () => {
  const [selectedPerson, setSelectedPerson] = useState<'tim' | 'diya' | null>(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, error } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPerson) return;

    setIsLoading(true);
    try {
      await login(selectedPerson, password);
    } catch (err) {
      // Error is handled in AuthContext
    } finally {
      setIsLoading(false);
      setPassword(''); // Clear password on error
    }
  };

  const handleSelectPerson = (person: 'tim' | 'diya') => {
    setSelectedPerson(person);
    setPassword('');
  };

  const getTheme = (person: 'tim' | 'diya') => {
    return person === 'diya'
      ? {
          primary: 'from-purple-500 to-pink-500',
          button: 'bg-purple-500 hover:bg-purple-600',
          border: 'border-purple-500',
          text: 'text-purple-900',
          bg: 'bg-purple-50',
        }
      : {
          primary: 'from-blue-500 to-teal-500',
          button: 'bg-blue-500 hover:bg-blue-600',
          border: 'border-blue-500',
          text: 'text-blue-900',
          bg: 'bg-blue-50',
        };
  };

  const currentTheme = selectedPerson ? getTheme(selectedPerson) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
       {/* Animated background hearts */}
      <BackgroundHearts />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className={`bg-gradient-to-r ${currentTheme?.primary || 'from-pink-500 to-purple-500'} rounded-3xl p-1 shadow-2xl transition-all duration-300`}>
          <div className="bg-white rounded-3xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">💕</div>
              <h1 className={`text-3xl font-bold ${currentTheme?.text || 'text-gray-900'} mb-2 transition-colors duration-300`}>
                {selectedPerson ? `Welcome, ${selectedPerson.charAt(0).toUpperCase() + selectedPerson.slice(1)}!` : 'I Miss You'}
              </h1>
              <p className="text-gray-600">
                {selectedPerson ? 'Enter your password' : 'Who are you?'}
              </p>
            </div>

            {/* Person Selection */}
            {!selectedPerson ? (
              <div className="space-y-4">
                <button
                  onClick={() => handleSelectPerson('diya')}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-4 px-6 rounded-xl transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  <span className="text-xl">💜 Diya</span>
                </button>
                
                <button
                  onClick={() => handleSelectPerson('tim')}
                  className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white font-semibold py-4 px-6 rounded-xl transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  <span className="text-xl">💙 Tim</span>
                </button>
              </div>
            ) : (
              /* Password Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className={`w-full px-4 py-3 border-2 ${currentTheme?.border} rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:border-transparent transition-all`}
                    autoFocus
                    required
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPerson(null);
                      setPassword('');
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                    disabled={isLoading}
                  >
                    Back
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isLoading || !password}
                    className={`
                      flex-1 ${currentTheme?.button} text-white font-semibold py-3 px-6 rounded-lg
                      transform transition-all duration-200
                      hover:scale-105 active:scale-95
                      shadow-lg hover:shadow-xl
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                    `}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'Sign In 💖'
                    )}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Secured by Firebase Authentication 🔐
              </p>
            </div>
          </div>
        </div>

        {/* Valentine's message */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600 italic">
            "Distance means so little when someone means so much" 💕
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;